import React, { useCallback, useEffect, useState } from "react";

import { fullscreenIcon } from "./icons";
import { ToolButton } from "./ToolButton";
import { Tooltip } from "./Tooltip";

type FullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

const FULLSCREEN_CHANGE_EVENTS = [
  "fullscreenchange",
  "webkitfullscreenchange",
  "mozfullscreenchange",
  "MSFullscreenChange",
] as const;

const getFullscreenElement = () => {
  if (typeof document === "undefined") {
    return null;
  }
  const doc = document as FullscreenDocument;
  return (
    doc.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.mozFullScreenElement ??
    doc.msFullscreenElement ??
    null
  );
};

const isFullscreenSupported = () => {
  if (typeof document === "undefined") {
    return false;
  }
  const element = document.documentElement as FullscreenElement | null;
  if (!element) {
    return false;
  }

  return Boolean(
    element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen,
  );
};

const requestFullscreen = async () => {
  if (typeof document === "undefined") {
    return;
  }
  const element = document.documentElement as FullscreenElement;
  const handler =
    element.requestFullscreen ||
    element.webkitRequestFullscreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullscreen;

  if (handler) {
    await handler.call(element);
  } else {
    throw new Error("Fullscreen API is not available.");
  }
};

const exitFullscreen = async () => {
  if (typeof document === "undefined") {
    return;
  }
  const doc = document as FullscreenDocument;
  const handler =
    doc.exitFullscreen ||
    doc.webkitExitFullscreen ||
    doc.mozCancelFullScreen ||
    doc.msExitFullscreen;

  if (handler) {
    await handler.call(doc);
  }
};

export const FullscreenButton = () => {
  const [isFullscreen, setIsFullscreen] = useState(() =>
    Boolean(getFullscreenElement()),
  );
  const [isSupported, setIsSupported] = useState(isFullscreenSupported);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    setIsSupported(isFullscreenSupported());

    const handleChange = () => {
      setIsFullscreen(Boolean(getFullscreenElement()));
    };

    FULLSCREEN_CHANGE_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName as any, handleChange);
    });

    return () => {
      FULLSCREEN_CHANGE_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName as any, handleChange);
      });
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!isSupported) {
      return;
    }

    try {
      if (getFullscreenElement()) {
        await exitFullscreen();
      } else {
        await requestFullscreen();
      }
    } catch (error) {
      console.warn("Unable to toggle fullscreen", error);
    }
  }, [isSupported]);

  if (!isSupported) {
    return null;
  }

  const label = isFullscreen ? "Exit full screen" : "Enter full screen";

  return (
    <Tooltip label={label}>
      <ToolButton
        type="icon"
        icon={fullscreenIcon}
        aria-label={label}
        onClick={toggleFullscreen}
        data-testid="fullscreen-toggle"
      />
    </Tooltip>
  );
};
